import React, { useEffect, useMemo, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { area as turfArea } from "@turf/turf";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";

const HABITAT_CARBON_PER_HA = {
  "Artificial unvegetated, unsealed surface": 28.14,
  "Cereal crops": 50.4,
  "Lowland meadows": 59.91,
  "Lowland mixed deciduous woodland": 228,
  "Mixed scrub": 61,
  "Modified grassland": 51,
  "Wood pasture and parkland": 142.5,
  "Vegetated garden": 50.4,
  "Traditional orchards": null,
  "Ornamental lake or pond": 25.64,
  "Other neutral grassland": 30.74,
  "Other coniferous woodland": 228,
  "Other woodland; broadleaved": 228,
  "Other woodland; mixed": 228,
};

const HABITAT_BIODIVERSITY_PER_HA = {
  "Artificial unvegetated, unsealed surface": 0,
  "Cereal crops": 2,
  "Lowland meadows": 16,
  "Lowland mixed deciduous woodland": 12,
  "Mixed scrub": 8,
  "Modified grassland": 4,
  "Wood pasture and parkland": 16,
  "Vegetated garden": 2,
  "Traditional orchards": 12,
  "Ornamental lake or pond": 4,
  "Other neutral grassland": 8,
  "Other coniferous woodland": 4,
  "Other woodland; broadleaved": 8,
  "Other woodland; mixed": 8,
};

const timestampMatchesBucket = (timestamp, selectedTimestamp, aggregationMethod) => {
  const date = new Date(timestamp);
  const selected = new Date(selectedTimestamp);
  if (Number.isNaN(date.getTime()) || Number.isNaN(selected.getTime())) return false;

  switch (aggregationMethod) {
    case "year":
      return date.getUTCFullYear() === selected.getUTCFullYear();
    case "month":
      return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth();
    case "day":
      return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate();
    case "hour":
      return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate() && date.getUTCHours() === selected.getUTCHours();
    default:
      return timestamp === selectedTimestamp;
  }
};

const readAggregatedAt = (series, timestamp, aggregationMethod) => {
  if (!series || !timestamp) return undefined;
  const values = Object.entries(series)
    .filter(([date]) => timestampMatchesBucket(date, timestamp, aggregationMethod))
    .map(([, value]) => value);
  if (!values.length) return undefined;
  if (typeof values[0] === "string") return values[0];

  const numericValues = values.filter((value) => typeof value === "number" && Number.isFinite(value));
  return numericValues.length ? numericValues.reduce((sum, value) => sum + value, 0) : undefined;
};

const formatScenarioPeriod = (timestamp, aggregationMethod) => {
  if (!timestamp) return "Unknown period";
  const date = new Date(timestamp);
  switch (aggregationMethod) {
    case "year": return String(date.getUTCFullYear());
    case "month": return date.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
    case "day": return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
    case "hour": return `${date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })}, ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} UTC`;
    default: return date.toLocaleString("en-GB", { timeZone: "UTC" });
  }
};

const LeftSubMenu = ({ isLeftSubDrawerOpen, globalState, globalRefs }) => {
  const SMSelectedArray = globalState.SMSelectedArray || [];
  const setSMSelectedArray = globalState.setSMSelectedArray;
  const setScenarioModelledFeatureIds = globalState.setScenarioModelledFeatureIds;
  const setScenarioHighlightedFeatureIds = globalState.setScenarioHighlightedFeatureIds;
  const confirmedScenarios = globalState.scenarioModels || [];
  const setConfirmedScenarios = globalState.setScenarioModels;
  const mapMetaData = globalState.mapMetaData;
  const selectedYear = globalState.selectedYearEditing;
  const numericTimeAggregationMethod = globalState.numericTimeAggregationMethod;
  const scenarioModelledViewEnabled = globalState.scenarioModelledViewEnabled;
  const setScenarioModelledViewEnabled = globalState.setScenarioModelledViewEnabled;
  const setScenarioModelingModeActive = globalState.setScenarioModelingModeActive;
  const featureSelectionMode = globalRefs.polygonCustomLayerRef;
  const features = globalRefs.longstandingGeoJSONRef?.current?.features || [];
  const [targetHabitat, setTargetHabitat] = useState("");
  const [expandedScenarioIds, setExpandedScenarioIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  useEffect(() => {
    setTargetHabitat("");
    setConfirmedScenarios([]);
    setExpandedScenarioIds([]);
    setSelectionMode(false);
    setSMSelectedArray([]);
    setScenarioModelledFeatureIds([]);
    setScenarioHighlightedFeatureIds([]);
    setConfirmedScenarios([]);
    featureSelectionMode.current?.alterScenarioFeatureSelectionMode(false);
    featureSelectionMode.current?.alterScenarioModelledFeatureIds([]);
  }, [mapMetaData, setSMSelectedArray, setScenarioModelledFeatureIds, setScenarioHighlightedFeatureIds, setConfirmedScenarios, setScenarioModelingModeActive, featureSelectionMode]);

  useEffect(() => {
    featureSelectionMode.current?.alterScenarioFeatureSelectionMode(
      isLeftSubDrawerOpen && selectionMode
    );
  }, [isLeftSubDrawerOpen, selectionMode, featureSelectionMode]);

  useEffect(() => {
    const ids = confirmedScenarios.flatMap((scenario) => scenario.features.map((feature) => feature.featureId));
    setScenarioModelledFeatureIds(ids);
    featureSelectionMode.current?.alterScenarioModelledFeatureIds(ids);
  }, [confirmedScenarios, featureSelectionMode, setScenarioModelledFeatureIds]);

  useEffect(() => {
    if (confirmedScenarios.length === 0 && scenarioModelledViewEnabled) {
      setScenarioModelledViewEnabled(false);
    }
  }, [confirmedScenarios, scenarioModelledViewEnabled, setScenarioModelledViewEnabled]);

  const selectedFeatures = useMemo(
    () => SMSelectedArray.map((id) => features.find((feature) => feature.skylark_id === id)).filter(Boolean),
    [SMSelectedArray, features]
  );

  const beginPicking = () => {
    if (!targetHabitat) return;
    setSMSelectedArray([]);
    setSelectionMode(true);
    featureSelectionMode.current?.alterScenarioFeatureSelectionMode(true);
  };

  const confirmSelection = () => {
    if (!targetHabitat || !selectedFeatures.length) return;
    const carbonPerHa = HABITAT_CARBON_PER_HA[targetHabitat];
    const biodiversityPerHa = HABITAT_BIODIVERSITY_PER_HA[targetHabitat];
    const changes = selectedFeatures.map((feature) => {
      const data = feature.feature_data_simple_timeseries || {};
      const currentLandtype = readAggregatedAt(data.HabitatType, selectedYear, numericTimeAggregationMethod);
      const currentParcelCarbonValue = readAggregatedAt(data.ParcelCarbon, selectedYear, numericTimeAggregationMethod);
      const currentBiodiversityValue = readAggregatedAt(data.BD_Parcel, selectedYear, numericTimeAggregationMethod);
      const currentParcelCarbon = Number(currentParcelCarbonValue);
      const currentBiodiversity = Number(currentBiodiversityValue);
      const geometryAreaHa = feature.geometry ? turfArea(feature.geometry) / 10000 : 0;
      return {
        featureId: feature.skylark_id,
        currentLandtype: currentLandtype ?? null,
        currentParcelCarbon: Number.isFinite(currentParcelCarbon) ? currentParcelCarbon : null,
        currentBiodiversity: Number.isFinite(currentBiodiversity) ? currentBiodiversity : null,
        newLandtype: targetHabitat,
        newParcelCarbon: carbonPerHa == null ? null : carbonPerHa * geometryAreaHa,
        newBiodiversity: biodiversityPerHa * geometryAreaHa,
        geometryAreaHa,
      };
    });
    const scenarioId = `scenario-${Date.now()}-${confirmedScenarios.length}`;
    setConfirmedScenarios((current) => {
      const selectedFeatureIds = new Set(changes.map((change) => String(change.featureId)));
      const withoutReplacedActions = current
        .map((scenario) => ({
          ...scenario,
          features: scenario.aggregationMethod === numericTimeAggregationMethod &&
            timestampMatchesBucket(scenario.selectedYear, selectedYear, numericTimeAggregationMethod)
            ? scenario.features.filter((change) => !selectedFeatureIds.has(String(change.featureId)))
            : scenario.features,
        }))
        .filter((scenario) => scenario.features.length > 0);
      return [...withoutReplacedActions, {
        id: scenarioId,
        targetHabitat,
        selectedYear,
        aggregationMethod: numericTimeAggregationMethod,
        features: changes,
      }];
    });
    const modelledIds = Array.from(new Set([
      ...(globalState.scenarioModelledFeatureIds || []),
      ...changes.map((change) => change.featureId),
    ]));
    setScenarioModelledFeatureIds((currentIds) => Array.from(new Set([
      ...(currentIds || []),
      ...changes.map((change) => change.featureId),
    ])));
    featureSelectionMode.current?.alterScenarioModelledFeatureIds(modelledIds);
    setSMSelectedArray([]);
    setSelectionMode(false);
    featureSelectionMode.current?.alterScenarioFeatureSelectionMode(false);
  };

  return (
    <Drawer
      open={isLeftSubDrawerOpen}
      direction="right"
      style={{ position: "absolute", top: "110px", right: globalState.isRighdivrawerVisible ? "378px" : "0px", height: "calc(100% - 100px)", width: "360px", backgroundColor: "#fff", zIndex: 3, boxShadow: "none", borderLeft: "3px solid #eee", overflowY: "auto" }}
      enableOverlay={false}
      duration={250}
    >
      <section style={{ padding: 16, color: "#8E285C" }}>
        <h2 style={{ margin: "4px 0 12px", fontSize: 18 }}>Scenario modelling</h2>
        <div style={{ marginBottom: 12, padding: 10, background: "#FFF1F7", border: "1px solid #F2B8D0", borderRadius: 4 }}>
          <label htmlFor="scenario-target-habitat" style={{ display: "block", fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Model change to</label>
          <select
            id="scenario-target-habitat"
            value={targetHabitat}
            onChange={(event) => setTargetHabitat(event.target.value)}
            disabled={selectionMode}
            style={{ width: "100%", padding: 9, border: "1px solid #E5A6C1", borderRadius: 4, color: "#8E285C", background: "#fff" }}
          >
            <option value="">Choose a habitat type</option>
            {Object.keys(HABITAT_CARBON_PER_HA).map((habitat) => <option key={habitat} value={habitat}>{habitat}</option>)}
          </select>
          <div style={{ marginTop: 8, fontSize: 12, color: "#687975" }}>
            for <strong style={{ color: "#8E285C" }}>{selectedYear ? formatScenarioPeriod(selectedYear, numericTimeAggregationMethod) : "Choose a period in the year scrubber"}</strong>
          </div>
        </div>

        {!selectionMode ? (
          <button type="button" disabled={!targetHabitat || !selectedYear} onClick={beginPicking} style={{ width: "100%", marginTop: 5, padding: 10, color: "white", background: targetHabitat && selectedYear ? "#C43D7D" : "#aaa", border: 0, borderRadius: 4, cursor: targetHabitat && selectedYear ? "pointer" : "not-allowed" }}>
            Select features
          </button>
        ) : (
          <>
            <p style={{ fontSize: 13 }}>Click map features to add them. {SMSelectedArray.length} selected.</p>
            <ul style={{ paddingLeft: 20, fontSize: 12 }}>{selectedFeatures.map((feature) => <li key={feature.skylark_id}>{feature.skylark_id}</li>)}</ul>
            <button type="button" disabled={!SMSelectedArray.length} onClick={confirmSelection} style={{ width: "100%", padding: 10, color: "white", background: SMSelectedArray.length ? "#C43D7D" : "#aaa", border: 0, borderRadius: 4 }}>Confirm selection</button>
            <button type="button" onClick={() => { setSelectionMode(false); setSMSelectedArray([]); featureSelectionMode.current?.alterScenarioFeatureSelectionMode(false); }} style={{ width: "100%", marginTop: 6, padding: 8, background: "white", border: "1px solid #aaa", borderRadius: 4 }}>Cancel</button>
          </>
        )}

        <h3 style={{ margin: "20px 0 8px", fontSize: 15 }}>Active Model Actions</h3>
        {confirmedScenarios.length > 0 && (
          <button
            type="button"
            aria-label="Scenario map view"
            aria-pressed={scenarioModelledViewEnabled}
            onClick={() => setScenarioModelledViewEnabled(!scenarioModelledViewEnabled)}
            style={{
              width: "100%",
              height: 34,
              marginBottom: 10,
              padding: "0 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: scenarioModelledViewEnabled ? "#fff" : "#D63384",
              backgroundColor: scenarioModelledViewEnabled ? "#D63384" : "#fff",
              border: "1px solid #D63384",
              borderRadius: 3,
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            <span>{scenarioModelledViewEnabled ? "Show Model Actions on 3D visual" : "Show Model Actions on 3D visual"}</span>
            <span style={{ position: "relative", width: 28, height: 14, borderRadius: 12, backgroundColor: scenarioModelledViewEnabled ? "rgba(255,255,255,0.35)" : "#F2B8D0", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 2, left: 2, width: 10, height: 10, borderRadius: "50%", backgroundColor: scenarioModelledViewEnabled ? "#fff" : "#D63384", transform: scenarioModelledViewEnabled ? "translateX(14px)" : "translateX(0)", transition: "transform 0.2s ease" }} />
            </span>
          </button>
        )}
        {confirmedScenarios.length === 0 ? <p style={{ fontSize: 12, color: "#777" }}>No active model actions yet.</p> : confirmedScenarios.map((scenario) => {
          const isExpanded = expandedScenarioIds.includes(scenario.id);
          const sumKnown = (field) => {
            const values = scenario.features.map((feature) => feature[field]).filter(Number.isFinite);
            return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
          };
          const beforeCarbon = sumKnown("currentParcelCarbon");
          const afterCarbon = sumKnown("newParcelCarbon");
          const carbonDifference = beforeCarbon == null || afterCarbon == null
            ? null
            : afterCarbon - beforeCarbon;
          const beforeBiodiversity = sumKnown("currentBiodiversity");
          const afterBiodiversity = sumKnown("newBiodiversity");
          const biodiversityDifference = beforeBiodiversity == null || afterBiodiversity == null
            ? null
            : afterBiodiversity - beforeBiodiversity;
          return (
              <article
              key={scenario.id}
              style={{ position: "relative", marginBottom: 10, background: "#FFF1F7", border: "1px solid #F2B8D0", borderRadius: 4, fontSize: 12, overflow: "hidden" }}
            >
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => {
                  const nextExpandedIds = isExpanded
                    ? expandedScenarioIds.filter((id) => id !== scenario.id)
                    : [...expandedScenarioIds, scenario.id];
                  setExpandedScenarioIds(nextExpandedIds);
                  setScenarioHighlightedFeatureIds(confirmedScenarios
                    .filter((item) => nextExpandedIds.includes(item.id))
                    .flatMap((item) => item.features.map((feature) => feature.featureId)));
                }}
                style={{ display: "block", width: "100%", padding: "10px 38px 10px 10px", textAlign: "left", background: "transparent", border: 0, color: "#8E285C", cursor: "pointer" }}
              >
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                  <strong style={{ textAlign: "left", marginRight: "auto" }}>Change to {scenario.targetHabitat}</strong>
                  <strong style={{ color: "#687975", whiteSpace: "nowrap" }}>
                    {formatScenarioPeriod(scenario.selectedYear, scenario.aggregationMethod)}
                  </strong>
                </span>
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <span>{scenario.features.length} feature{scenario.features.length === 1 ? "" : "s"}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 5, color: "#687975" }}>
                      {isExpanded ? <IoIosArrowDown aria-hidden="true" /> : <IoIosArrowForward aria-hidden="true" />}
                      {isExpanded ? "Hide feature details" : "Show feature details"}
                    </span>
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginLeft: "auto", textAlign: "right" }}>
                    <span style={{ marginRight: 0 }}>Carbon: {carbonDifference == null ? "Unavailable" : <strong style={{ color: carbonDifference > 0 ? "#218739" : carbonDifference < 0 ? "#d12f2f" : "#687975" }}>{carbonDifference > 0 ? "↑ " : carbonDifference < 0 ? "↓ " : "→ "}{carbonDifference > 0 ? "+" : ""}{carbonDifference.toFixed(3)}</strong>}</span>
                    <span>Biodiversity: {biodiversityDifference == null ? "Unavailable" : <strong style={{ color: biodiversityDifference > 0 ? "#218739" : biodiversityDifference < 0 ? "#d12f2f" : "#687975" }}>{biodiversityDifference > 0 ? "↑ " : biodiversityDifference < 0 ? "↓ " : "→ "}{biodiversityDifference > 0 ? "+" : ""}{biodiversityDifference.toFixed(3)}</strong>}</span>
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label={`Delete scenario changing to ${scenario.targetHabitat}`}
                title="Delete scenario"
                onClick={(event) => {
                  event.stopPropagation();
                  const remainingScenarios = confirmedScenarios.filter((item) => item.id !== scenario.id);
                  const nextExpandedIds = expandedScenarioIds.filter((id) => id !== scenario.id);
                  setConfirmedScenarios(remainingScenarios);
                  setExpandedScenarioIds(nextExpandedIds);
                  setScenarioHighlightedFeatureIds(remainingScenarios
                    .filter((item) => nextExpandedIds.includes(item.id))
                    .flatMap((item) => item.features.map((feature) => feature.featureId)));
                }}
                style={{ position: "absolute", top: 5, right: 6, zIndex: 1, width: 24, height: 24, padding: 0, border: 0, borderRadius: "50%", background: "#F8D7E6", color: "#8E285C", fontSize: 18, lineHeight: "22px", cursor: "pointer" }}
              >
                ×
              </button>
              {isExpanded && <div style={{ padding: "0 10px 10px" }}>
                {scenario.features.map((change) => (
                  <div key={change.featureId} style={{ padding: "8px 0", borderTop: "1px solid #F2B8D0" }}>
                    <strong>{change.featureId}</strong>
                    <div>{change.currentLandtype || "Unknown habitat"} → {change.newLandtype}</div>
                    <div>ParcelCarbon: {change.currentParcelCarbon == null ? "Unavailable" : change.currentParcelCarbon.toFixed(3)} → {change.newParcelCarbon == null ? "Unavailable (no carbon value in table)" : change.newParcelCarbon.toFixed(3)}</div>
                    <div>BD_Parcel: {change.currentBiodiversity == null ? "Unavailable" : change.currentBiodiversity.toFixed(3)} → {change.newBiodiversity.toFixed(3)}</div>
                  </div>
                ))}
              </div>}
            </article>
          );
        })}
      </section>
    </Drawer>
  );
};

export default LeftSubMenu;
